import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Local hint that a session cookie *might* exist. Skipping /auth/me when this is
// absent avoids the noisy 401 the browser logs at the network layer for
// first-time / logged-out visitors. Set on login/signup, cleared on logout.
const SESSION_HINT_KEY = "sa_session_hint";
const hasSessionHint = () => {
  try { return localStorage.getItem(SESSION_HINT_KEY) === "1"; } catch { return false; }
};
const setSessionHint = (v) => {
  try { v ? localStorage.setItem(SESSION_HINT_KEY, "1") : localStorage.removeItem(SESSION_HINT_KEY); } catch {}
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip the ping when we have no reason to believe a session exists.
    if (!hasSessionHint()) {
      setUser(false);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setSessionHint(true);
      return data;
    } catch {
      setUser(false);
      setSessionHint(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A dead session (15-min idle timeout / revoked) → drop to logged-out state.
  useEffect(() => {
    const handler = () => {
      setUser(false);
      setSessionHint(false);
    };
    window.addEventListener("sa:session-expired", handler);
    return () => window.removeEventListener("sa:session-expired", handler);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) return data; // caller shows the code-entry step
    setUser(data);
    setSessionHint(true);
    return data;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    setUser(data);
    setSessionHint(true);
    return data;
  };

  const resendOtp = async (email) => {
    const { data } = await api.post("/auth/resend-otp", { email });
    return data;
  };

  const signup = async (company_name, name, email, password, accepted_tos) => {
    let timezone = null;
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* old browsers */ }
    const { data } = await api.post("/auth/signup", {
      company_name,
      name,
      email,
      password,
      accepted_tos,
      timezone,
    });
    setUser(data);
    setSessionHint(true);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    setSessionHint(false);
  };

  const completeOnboarding = async (name, password) => {
    const { data } = await api.post("/auth/complete-onboarding", { name, password });
    setUser(data.user);
    setSessionHint(true);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, resendOtp, signup, logout, refresh, completeOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
